import { Badge, Card, CardContent } from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { PiCheckCircle, PiWarning, PiXCircle } from 'react-icons/pi';
import { MonitorCardHeader } from './MonitorCardHeader';

interface ThresholdMonitorProps {
  name: string;
  description?: string;
  alarmThreshold?: number;
  warningThreshold?: number;
  currentValue: number;
  unit?: string;
  className?: string;
}

export function ThresholdMonitor({
  name,
  description,
  alarmThreshold,
  warningThreshold,
  currentValue,
  unit = '',
  className,
}: ThresholdMonitorProps) {
  // Determine status based on thresholds
  const getStatus = () => {
    if (alarmThreshold !== undefined && currentValue > alarmThreshold) {
      return { level: 'alarm', text: 'Alarm', threshold: alarmThreshold };
    }
    if (warningThreshold !== undefined && currentValue > warningThreshold) {
      return { level: 'warning', text: 'Warning', threshold: warningThreshold };
    }
    return {
      level: 'normal',
      text: 'Normal',
      threshold: alarmThreshold || warningThreshold || 0,
    };
  };

  const status = getStatus();
  const activeThreshold = status.threshold;

  // Calculate percentage for progress bar
  const percentage =
    activeThreshold > 0
      ? Math.min((currentValue / activeThreshold) * 100, 100)
      : 0;

  const getStatusIcon = () => {
    switch (status.level) {
      case 'alarm':
        return <PiXCircle className="h-4 w-4 text-destructive" />;
      case 'warning':
        return <PiWarning className="h-4 w-4 text-yellow-600" />;
      default:
        return <PiCheckCircle className="h-4 w-4 text-green-600" />;
    }
  };

  const getValueColor = () => {
    switch (status.level) {
      case 'alarm':
        return 'text-destructive';
      case 'warning':
        return 'text-yellow-600';
      default:
        return 'text-foreground';
    }
  };

  const getProgressColor = () => {
    switch (status.level) {
      case 'alarm':
        return 'bg-destructive';
      case 'warning':
        return 'bg-yellow-500';
      default:
        return 'bg-green-600';
    }
  };

  const getBadgeVariant = () => {
    switch (status.level) {
      case 'alarm':
        return 'destructive';
      case 'warning':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Card className={cn('w-full', className)}>
      <MonitorCardHeader
        name={name}
        description={description}
        statusIcon={getStatusIcon()}
      />
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-baseline space-x-2">
            <span className={cn('text-2xl font-bold', getValueColor())}>
              {currentValue.toLocaleString()}
            </span>
            {unit && (
              <span className="text-sm text-muted-foreground ml-1">{unit}</span>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Threshold</span>
              <span className="font-medium">
                {activeThreshold.toLocaleString()}
                {unit && ` ${unit}`}
              </span>
            </div>

            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  getProgressColor()
                )}
                style={{
                  width: `${Math.min(Math.max(percentage, 0), 100)}%`,
                }}
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {percentage.toFixed(1)}% of threshold
              </span>
              <Badge
                variant={getBadgeVariant()}
                className={cn(
                  status.level === 'normal' &&
                    'bg-green-100 text-green-800 hover:bg-green-100',
                  status.level === 'warning' &&
                    'bg-yellow-100 text-yellow-800 hover:bg-yellow-100'
                )}
              >
                {status.text}
              </Badge>
            </div>

            {/* Show both thresholds if available */}
            {warningThreshold !== undefined && alarmThreshold !== undefined && (
              <div className="text-xs text-muted-foreground space-y-1">
                <div>
                  Warning: {warningThreshold.toLocaleString()}
                  {unit && ` ${unit}`}
                </div>
                <div>
                  Alarm: {alarmThreshold.toLocaleString()}
                  {unit && ` ${unit}`}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
